<template>
  <v-card>
    <v-card-title class="bg-blue-grey-lighten-5 text-primary">
      {{ t('chat.pickupModal.title') }}
    </v-card-title>

    <v-tabs v-model="tabs" fixed-tabs color="primary">
      <v-tab value="dropOff">{{ t('chat.pickupModal.tabs.dropOff') }}</v-tab>
      <v-tab value="pickup">{{ t('chat.pickupModal.tabs.pickup') }}</v-tab>
    </v-tabs>

    <v-card-text class="py-3"style="height: 480px; overflow-y: auto;">
      <v-window v-model="tabs" :transition="false":reverse-transition="false">
        
        <v-window-item value="dropOff">
          <v-form ref="post_dropoff_form" class="pt-2">
            <v-menu
              v-model="isDropOffDatePicker"
              :close-on-content-click="false"
              transition="scale-transition"
            >
              <template v-slot:activator="{ props }">
                <v-text-field
                  :model-value="dropOffDateFormatted"
                  :label="t('chat.pickupModal.fields.date')"
                  prepend-icon="mdi-calendar"
                  :rules="[rules.required]"
                  variant="outlined"
                  density="compact"
                  readonly
                  v-bind="props"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="dropOffDateVal"
                :locale="datePickerLocale"
                :key="`dropoff-picker-${datePickerLocale}`"
                :min="allowMinDate"
                title=""
                header=""
                @update:model-value="onDropOffDateChange"
              ></v-date-picker>
            </v-menu>
            <div class="mt-4"></div>

            <v-select
              v-model="dropOffById"
              :items="parentChoices"
              item-title="value"
              item-value="id"
              :rules="[rules.required]"
              :label="t('chat.pickupModal.fields.dropOffBy')"
              :loading="fetchLoading"
              variant="outlined"
              density="compact"
            ></v-select>

            <div class="mt-4"></div>

            <time-picker
              v-model="dropOffTime"
              :label="t('chat.pickupModal.fields.dropOffTime')"
              prepend-icon="mdi-clock-time-four-outline"
              :rules="[rules.required]"
              variant="outlined"
              density="compact"
            />

            <div class="mt-4"></div>

            <v-textarea
              v-model="dropOffReason"
              :rules="[rules.required, rules.counter100]"
              :label="t('chat.pickupModal.fields.reason')"
              auto-grow
              variant="outlined"
              density="compact"
            />
          </v-form>
        </v-window-item>

        <v-window-item value="pickup">
          <v-form ref="post_pickup_form" class="pt-2">
            <v-menu
              v-model="isPickupDatePicker"
              :close-on-content-click="false"
              transition="scale-transition"
            >
              <template v-slot:activator="{ props }">
                <v-text-field
                  :model-value="pickupDateFormatted"
                  :label="t('chat.pickupModal.fields.date')"
                  prepend-icon="mdi-calendar"
                  :rules="[rules.required]"
                  variant="outlined"
                  density="compact"
                  readonly
                  v-bind="props"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="pickupDateVal"
                :locale="datePickerLocale"
                :key="`pickup-picker-${datePickerLocale}`"
                :min="allowMinDate"
                title=""
                header=""
                @update:model-value="onPickupDateChange"
              ></v-date-picker>
            </v-menu>
            <div class="mt-4"></div>

            <v-select
              v-model="pickupById"
              :items="parentChoices"
              item-title="value"
              item-value="id"
              :rules="[rules.required]"
              :label="t('chat.pickupModal.fields.pickupBy')"
              :loading="fetchLoading"
              variant="outlined"
              density="compact"
            ></v-select>

            <div class="mt-4"></div>

            <time-picker
              v-model="pickupTime"
              :label="t('chat.pickupModal.fields.pickupTime')"
              prepend-icon="mdi-clock-time-four-outline"
              :rules="[rules.required]"
              variant="outlined"
              density="compact"
            />

            <div class="mt-4"></div>

            <v-textarea
              v-model="pickupReason"
              :rules="[rules.required, rules.counter100]"
              :label="t('chat.pickupModal.fields.reason')"
              auto-grow
              variant="outlined"
              density="compact"
            />
          </v-form>
        </v-window-item>
      </v-window>
    </v-card-text>

    <v-divider></v-divider>

    <v-card-actions>
      <div style="width: 20px;"></div>
      <v-btn @click="close" color="error" variant="text"> {{ t('common.cancel') }} </v-btn>
      <div style="width: 20px;"></div>
      <v-btn @click="clickPost" :loading="btnLoading" color="primary" variant="flat">
        {{ t('common.submit') }}
      </v-btn>
      <div style="width: 20px;"></div>
    </v-card-actions>
  </v-card>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { getCurrentPickupTimeDefault, toPickupISOString } from '@/utils/dayjsTz';
import { formatDisplayDate } from '@/utils/dateFormat';
import { t as translate, calendarLocale, getAppLocale } from '@/i18n';
import { translateParentRelationLabel } from '@/i18n/parentRelationLabel';
import rules from '@/service/ValidRules';
import TimePicker from '@/components/molecules/TimePicker.vue';
import { chatModule } from '@/store/entityModules/chat';
import { meModule } from '@/store/entityModules/me';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import 'dayjs/locale/en';
import 'dayjs/locale/km';

export default defineComponent({
  name: 'PickupModal',
  components: { TimePicker },
  props: {
    isOpen: { type: Boolean, default: false },
    childId: { type: Number, default: NaN },
    selectedDate: { type: String, default: dayjs().format('YYYY-MM-DD') },
  },
  emits: ['close', 'posted'],

  data() {
    return {
      tabs: 'dropOff',
      dropOffDate: '',
      dropOffDateVal: null as any,
      pickupDate: '',
      pickupDateVal: null as any,
      dropOffById: null as any,
      dropOffTime: getCurrentPickupTimeDefault(),
      pickupById: null as any,
      pickupTime: getCurrentPickupTimeDefault(),
      dropOffReason: '',
      pickupReason: '',
      fetchLoading: false,
      isDropOffDatePicker: false,
      isPickupDatePicker: false,
      btnLoading: false,
    };
  },

  computed: {
    datePickerLocale(): string {
      void meModule.me?.language;
      const loc = getAppLocale();
      dayjs.locale(loc);
      return calendarLocale(loc);
    },
    rules() { return rules; },
    allowMinDate() { return new Date(); },
    parentChoices() {
      return chatModule.parentChoice.map((row) => ({
        id: row.id,
        value: translateParentRelationLabel(row.value),
      }));
    },
    /** Display by locale; internal data is still YYYY-MM-DD */
    dropOffDateFormatted() {
      return formatDisplayDate(this.dropOffDate);
    },
    pickupDateFormatted() {
      return formatDisplayDate(this.pickupDate);
    },
  },

  watch: {
    isOpen: {
      async handler(val: boolean) {
        if (val) {
          this.resetForm();
          if (this.parentChoices.length === 0) {
            this.fetchLoading = true;
            await chatModule.fetchChatsChoices();
            this.fetchLoading = false;
          }
        }
      },
      immediate: true,
    },
  },

  methods: {
    t(path: string, fallback = path, params: Record<string, string | number> = {}) {
      return translate(path, fallback, params);
    },
    resetForm() {
      // 初期値として props の selectedDate をセットすると親切です
      this.dropOffDate = this.selectedDate;
      this.pickupDate = this.selectedDate;
      // カレンダーの初期位置も合わせる
      this.dropOffDateVal = new Date(this.selectedDate);
      this.pickupDateVal = new Date(this.selectedDate);
      
      this.dropOffById = null;
      this.pickupById = null;
      this.dropOffReason = '';
      this.pickupReason = '';
    },
    onDropOffDateChange(val: any) {
      if (!val) return;
      this.dropOffDate = dayjs(val).format('YYYY-MM-DD');
      this.isDropOffDatePicker = false;
    },
    onPickupDateChange(val: any) {
      if (!val) return;
      this.pickupDate = dayjs(val).format('YYYY-MM-DD');
      this.isPickupDatePicker = false;
    },
    close() {
      this.$emit('close');
    },
    async clickPost() {
      // 適切なフォームのバリデーションを実行
      const formRef = this.tabs === 'dropOff' 
        ? (this.$refs.post_dropoff_form as any) 
        : (this.$refs.post_pickup_form as any);
      
      if (!formRef) return;
      
      const { valid } = await formRef.validate();
      if (!valid) return;

      this.btnLoading = true;
      try {
        const isDropOff = this.tabs === 'dropOff';
        const targetDate = isDropOff ? this.dropOffDate : this.pickupDate;
        const targetTimeStr = isDropOff ? this.dropOffTime : this.pickupTime;
        
        const isoTime = toPickupISOString(targetDate, targetTimeStr);

        await chatModule.putChatsPickup({
          childId: this.childId,
          date: targetDate,
          dropOffById: isDropOff ? this.dropOffById : null,
          dropOffTime: isDropOff ? isoTime : null,
          pickupById: isDropOff ? null : this.pickupById,
          pickupTime: isDropOff ? null : isoTime,
          reason: isDropOff ? this.dropOffReason : this.pickupReason,
        });

        this.$emit('posted');
        this.$emit('close');
      } catch (error) {
        console.error("Post Error:", error);
      } finally {
        this.btnLoading = false;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
:deep(.v-date-picker) {
  max-width: 100%;
}
:deep(.v-card) {
  display: flex;
  flex-direction: column;
}

/* テキストエリアのリサイズでボトムナビが動くのを防ぐ */
:deep(.v-textarea textarea) {
  resize: none !important;
}
</style>